/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
// Coverage review (Paula 2026-10-20): who is working which day for the next
// two weeks, and HOW each day off got in (driver entered / request approved /
// office entered) and WHEN -- so scheduling is reviewed on a rhythm, not
// discovered the morning of. Thursday 8 AM text links here.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { todayET } from '../utils/et';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const dayLabel = (d) => {
  const dt = new Date(d + 'T12:00:00Z');
  return { dow: DOW[dt.getUTCDay()], md: `${dt.getUTCMonth() + 1}/${dt.getUTCDate()}`, weekend: [0, 6].includes(dt.getUTCDay()) };
};
const addDays = (d, n) => { const t = new Date(d + 'T12:00:00Z'); t.setUTCDate(t.getUTCDate() + n); return t.toISOString().slice(0, 10); };
const mondayOf = (d) => { const w = new Date(d + 'T12:00:00Z').getUTCDay(); return addDays(d, w === 0 ? -6 : 1 - w); };
const short = (w) => {
  if (!w) return '';
  const fmt = (t) => { const [h, m] = t.split(':').map(Number); const hh = h % 12 || 12; return m ? `${hh}:${String(m).padStart(2, '0')}` : `${hh}`; };
  const [a, b] = w.split('-');
  return a && b ? `${fmt(a)}-${fmt(b)}` : w;
};

const STATE = {
  work:        { bg: '#E1F5EE', color: '#0F6E56', text: (c) => short(c.window) || 'In' },
  partial:     { bg: '#FAEEDA', color: '#854F0B', text: (c) => 'Part' },
  off:         { bg: '#FCEBEB', color: '#A32D2D', text: (c) => c.note === 'called in' ? 'Called in' : 'OFF' },
  regular_off: { bg: '#F5F5F3', color: '#B4B2A9', text: () => '' },
  not_offered: { bg: '#F5F5F3', color: '#888780', text: () => 'not offered' },
  not_entered: { bg: '#FFF7D6', color: '#7A5B00', text: () => '?' },
  not_used:    { bg: '#FAFAF8', color: '#C9C7BF', text: () => '' },
};
const SOURCE_LABEL = { 'driver entered': 'Driver entered', 'request approved': 'Request approved', 'office entered': 'Office entered', 'request PENDING': 'Request pending' };

export default function Coverage() {
  const [start, setStart] = useState(mondayOf(todayET()));
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState(null); // { driver, day, cell }

  const load = useCallback(async () => {
    setError('');
    try {
      const r = await api.get(`/drivers/coverage?start=${start}&days=14`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load coverage');
    }
  }, [start]);
  useEffect(() => { load(); }, [load]);

  const days = data?.days || [];
  const drivers = (data?.drivers || []).filter(r => !r.days.every(c => ['regular_off', 'not_used'].includes(c.state)));
  const idle = (data?.drivers || []).filter(r => r.days.every(c => ['regular_off', 'not_used'].includes(c.state)));
  const missing = drivers.filter(r => r.days.some(c => c.state === 'not_entered'));
  const weekAgo = Date.now() - 7 * 86400000;
  const recent = [];
  (data?.drivers || []).forEach(r => r.days.forEach((c, i) => {
    if (['off', 'partial'].includes(c.state) && c.enteredAt && new Date(c.enteredAt).getTime() > weekAgo) recent.push({ name: r.name, day: days[i], c });
  }));

  return (
    <div style={{ paddingTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 500, margin: 0 }}>Coverage</h1>
          <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>Who is working, two weeks at a time. Tap a cell for how it got in.</div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => setStart(addDays(start, -7))} style={{ fontSize: 12, padding: '6px 10px' }}>Prev week</button>
          <button onClick={() => setStart(mondayOf(todayET()))} style={{ fontSize: 12, padding: '6px 10px' }}>This week</button>
          <button onClick={() => setStart(addDays(start, 7))} style={{ fontSize: 12, padding: '6px 10px' }}>Next week</button>
        </div>
      </div>

      {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>}
      {!data && !error && <div style={{ color: '#999', fontSize: 13, padding: '2rem', textAlign: 'center' }}>Loading...</div>}

      {data && (
        <>
          {missing.length > 0 && (
            <div style={{ background: '#FFF7D6', border: '0.5px solid #F0D98A', borderRadius: 10, padding: '10px 12px', marginBottom: 12, fontSize: 13 }}>
              <b>Not entered yet:</b> {missing.map(r => {
                const n = r.days.filter(c => c.state === 'not_entered').length;
                return `${r.name} (${n} day${n === 1 ? '' : 's'})`;
              }).join(', ')}
              <div style={{ fontSize: 11, color: '#7A5B00', marginTop: 2 }}>Flex drivers get a reminder text Wednesday at 5 PM for the coming week.</div>
            </div>
          )}

          {recent.length > 0 && (
            <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 10, padding: '10px 12px', marginBottom: 12, fontSize: 13 }}>
              <b>Time off entered in the last 7 days</b>
              {recent.map((x, i) => {
                const l = dayLabel(x.day);
                return (
                  <div key={i} style={{ fontSize: 12, color: '#555', marginTop: 4 }}>
                    {x.name}: {x.c.state === 'partial' ? x.c.note : 'off'} {l.dow} {l.md} &middot; {SOURCE_LABEL[x.c.source] || x.c.source} {x.c.entered}
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ overflowX: 'auto', background: 'white', border: '0.5px solid #e0e0db', borderRadius: 10, WebkitOverflowScrolling: 'touch' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 900, width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ position: 'sticky', left: 0, background: 'white', textAlign: 'left', padding: '6px 8px', zIndex: 1, borderBottom: '0.5px solid #e0e0db' }}>Driver</th>
                  {days.map(d => {
                    const l = dayLabel(d);
                    return (
                      <th key={d} style={{ padding: '6px 4px', borderBottom: '0.5px solid #e0e0db', borderLeft: d === addDays(start, 7) ? '2px solid #185FA5' : 'none', background: l.weekend ? '#FAFAF8' : 'white', fontWeight: 500 }}>
                        <div>{l.dow}</div><div style={{ color: '#999', fontWeight: 400 }}>{l.md}</div>
                        <div style={{ color: '#0F6E56', fontWeight: 600, marginTop: 2 }}>{data.perDay[d].working}</div>
                        {(data.perDay[d].extra || []).length > 0 && <div style={{ color: '#999', fontWeight: 400, fontSize: 9 }}>+{data.perDay[d].extra.length}</div>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {drivers.map(r => (
                  <tr key={r.id}>
                    <td style={{ position: 'sticky', left: 0, background: 'white', padding: '4px 8px', whiteSpace: 'nowrap', borderBottom: '0.5px solid #f0f0ec', zIndex: 1 }}>
                      {r.name}{r.flex && <span style={{ color: '#999', fontSize: 9, marginLeft: 4 }}>flex</span>}
                      {r.onCall && <span style={{ color: '#999', fontSize: 9, marginLeft: 4 }}>on-call</span>}
                    </td>
                    {r.days.map((c, i) => {
                      // Working but not day crew (on-call or a short window): muted
                      const st = (['work', 'partial'].includes(c.state) && c.dayCrew === false)
                        ? { bg: '#F3F8F5', color: '#6E8F82', text: STATE[c.state].text }
                        : (STATE[c.state] || STATE.regular_off);
                      return (
                        <td key={i} onClick={() => setPicked({ driver: r, day: days[i], cell: c })}
                          style={{ padding: 2, borderBottom: '0.5px solid #f0f0ec', borderLeft: days[i] === addDays(start, 7) ? '2px solid #185FA5' : 'none', cursor: 'pointer' }}>
                          <div style={{ background: st.bg, color: st.color, borderRadius: 4, padding: '4px 2px', textAlign: 'center', minHeight: 14, fontWeight: c.state === 'off' ? 600 : 400, whiteSpace: 'nowrap' }}>
                            {st.text(c)}{c.source === 'driver entered' && ['off', 'partial'].includes(c.state) ? ' •' : ''}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {data.office.length > 0 && (
                  <tr>
                    <td style={{ position: 'sticky', left: 0, background: 'white', padding: '4px 8px', fontStyle: 'italic', color: '#666' }}>Office</td>
                    {days.map(d => {
                      const o = data.perDay[d].office;
                      return <td key={d} style={{ padding: 2 }}>{o.length > 0 && <div style={{ background: '#EDF4FB', color: '#185FA5', borderRadius: 4, padding: '4px 2px', textAlign: 'center' }}>{o.join(', ')} off</div>}</td>;
                    })}
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 11, color: '#666', marginTop: 8 }}>
            <span><span style={{ background: STATE.work.bg, padding: '1px 6px', borderRadius: 3 }}>9-5</span> working</span>
            <span><span style={{ background: STATE.partial.bg, padding: '1px 6px', borderRadius: 3 }}>Part</span> out part of the day</span>
            <span><span style={{ background: STATE.off.bg, padding: '1px 6px', borderRadius: 3 }}>OFF</span> day off</span>
            <span>• = driver entered it himself (no request)</span>
            <span><span style={{ background: STATE.not_entered.bg, padding: '1px 6px', borderRadius: 3 }}>?</span> flex, not entered yet</span>
            <span><span style={{ background: STATE.not_offered.bg, padding: '1px 6px', borderRadius: 3 }}>not offered</span> flex, left the day blank</span>
            <span><span style={{ background: '#F3F8F5', color: '#6E8F82', padding: '1px 6px', borderRadius: 3 }}>4-9</span> on-call or short day (not counted)</span>
            <span>Green number = day crew: in at least 6 hours between 7 AM and 7 PM, not on-call. +N = others working</span>
          </div>
          {idle.length > 0 && (
            <div style={{ fontSize: 11, color: '#999', marginTop: 8 }}>
              Not scheduled at all these two weeks: {idle.map(r => r.name).join(', ')}
            </div>
          )}
        </>
      )}

      {picked && (
        <div onClick={() => setPicked(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 50 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: '12px 12px 0 0', padding: 16, width: '100%', maxWidth: 480, fontSize: 14 }}>
            <div style={{ fontWeight: 500, marginBottom: 6 }}>{picked.driver.fullName} &middot; {dayLabel(picked.day).dow} {dayLabel(picked.day).md}</div>
            <div style={{ color: '#555' }}>
              {({
                work: `Working${picked.cell.window ? ' ' + picked.cell.window : ''}`,
                partial: `Working, ${picked.cell.note}`,
                off: `Off (${picked.cell.note})`,
                regular_off: 'Not a scheduled day',
                not_offered: 'Flex driver left this day blank (not working)',
                not_entered: 'Flex driver has not entered this day yet',
                not_used: 'Flex driver who has not used Availability lately',
              })[picked.cell.state]}
            </div>
            {picked.cell.source && (
              <div style={{ color: '#888', fontSize: 12, marginTop: 6 }}>{SOURCE_LABEL[picked.cell.source] || picked.cell.source}{picked.cell.entered ? ` on ${picked.cell.entered}` : ''}</div>
            )}
            {picked.driver.flex && picked.driver.lastEntered && (
              <div style={{ color: '#888', fontSize: 12, marginTop: 2 }}>Has entered availability through {picked.driver.lastEntered.slice(5).replace('-', '/')}</div>
            )}
            <button onClick={() => setPicked(null)} style={{ marginTop: 12, width: '100%', padding: 10, fontSize: 14 }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
